import axios from "axios";
import {addAuthKey} from '@/helpers/addAuthKeyToUrl';
import { reformatDateForServer } from "@/helpers/workWithDates";
import { getQueryParamsForFetchWorkers } from "@/pages/violations/violationsFunctions";
import { ViolationTypeDescription } from "@/entities/models/violation-type";

const apiUrl = "index.php";

const tableColumnSortingArray = ["fio", "post", "filial", "date", "foul", "points", "addedBy", "deadline"];

const appendViolationDataToFormData = (violationData) => {
    const formData = new FormData();

    formData.append("date", reformatDateForServer(violationData.date));
    formData.append("filial_id", violationData.filial.value);
    formData.append("worker_id", violationData.worker.value);
    formData.append("explanatory_required", violationData.isExplanatoryRequired ? 1 : 0);
    formData.append("foul_id", violationData.violation.value);
    if (violationData.deadline) {
        formData.append("deadline", reformatDateForServer(violationData.deadline));
    }

    return formData;
};

const appendViolationUpdatingData = (violationData) => {
    const formData = new FormData();

    formData.append("foul_id", violationData.violation.value);
    formData.append("date", reformatDateForServer(violationData.date));

    return formData;
};

const state = () => ({
    collections: {
        foulTypes: {
            groups: [],
            items: [],
        },
        filials: [],
        positions: [],
        workers: [],
    },
    records: [],
    columns: [],
    userColumns: [],
    queryForGoogleSheets: [],
    isMoreDataAvailable: true,
    totalCount: 0,
});

const actions = {
    async fetchWorkerRecords({ rootState, commit, state }, filters) {
        const token = rootState.authToken;
        const queryParams = getQueryParamsForFetchWorkers(filters);
        const url = addAuthKey(`${apiUrl}?r=api/worker-foul/index&per-page=30&${queryParams.join("&")}`, token);

        commit("setQueryForGoogleSheets", queryParams);

        const { data, headers } = await axios.get(url);
        const totalItemsCount = +headers["x-pagination-total-count"];

        commit("setTotalCount", totalItemsCount);

        if (filters.page === 1) {
            commit("setFirstRecords", data);
        } else {
            commit("setRecords", data);
        }

        if (totalItemsCount > state.records.length) {
            commit("setIsMoreDataAvailable", true);
        } else {
            commit("setIsMoreDataAvailable", false);
        }
    },
    async fetchFoulTypes({ rootState, commit }) {
        const token = rootState.authToken;
        const foulsData = Promise.all([
            axios.get(addAuthKey(`${apiUrl}?r=api/foul-category/index&pagination=0`, token)),
            axios.get(addAuthKey(`${apiUrl}?r=api/foul/index&pagination=0`, token)),
        ]);

        const response = await foulsData;
        const [foulCategories, foulTypes] = response;

        const foulGroups = foulCategories.data
            .sort((a, b) => b.sort - a.sort)
            .map((c) => ({ label: c.name, name: c.id.toString() }));
        const foulItems = foulTypes.data.map((t) => {
            const { description: label, id: value, category_id } = t;
            const descriptionModel = ViolationTypeDescription(t);
            return {
                descriptionModel,
                label,
                value,
                group: category_id ? category_id.toString() : "empty",
                subLabel: descriptionModel.description || descriptionModel.link || descriptionModel.files.length,
            };
        });

        foulGroups.push({
            label: "Без категории",
            name: "empty",
        });

        commit("setFoulTypesCollection", { groups: foulGroups, items: foulItems });
    },
    async fetchFilials({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/filial/index`, token);

        const { data } = await axios.get(url);

        const filials = data.items.map((filial) => ({ label: filial.filial_name, value: filial.id }));

        commit("setFilialsCollection", filials);
    },
    async fetchPositions({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/directory/position`, token);

        const { data } = await axios.get(url);
        const positions = Object.entries(data).map(([positionCode, { name }]) => ({
            label: name,
            value: positionCode,
        }));

        commit("setPositionsCollection", positions);
    },
    async fetchWorkers({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/worker/index&pagination=0&sort=name&job_status=1&is_filter=1`, token);

        const { data } = await axios.get(url);
        const workers = data.map((worker) => ({
            label: worker.name,
            value: worker.id,
            filialId: worker.filial_id,
            combination_filials: worker.combination_filials 
        }));
        commit("setWorkersCollection", workers);
    },
    async fetchColumns({ rootState, commit }, userId) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/worker-foul/columns&user_id=${userId}`, token);

        const { data } = await axios.get(url);
        const columns = data
            .map((column) => ({
                id: column.code,
                label: column.short_name,
                fullName: column.name,
                type: column.data_type,
                level: column.level,
                isCategory: column.is_category,
                disabled: column.disabled,
            }))
            .sort((a, b) => tableColumnSortingArray.indexOf(a.id) - tableColumnSortingArray.indexOf(b.id));

        commit("setColumns", columns);
    },
    async fetchUserColumns({ rootState, commit }) {
        const token = rootState.authToken;
        const { userId } = rootState;
        const url = addAuthKey(`${apiUrl}?r=api/worker-foul/user-columns&user_id=${userId}`, token);

        const { data } = await axios.get(url);

        commit("setUserColumns", data.columns);
    },
    async saveUserColumns({ rootState, commit }, userColumns) {
        const token = rootState.authToken;
        const { userId } = rootState;
        const url = addAuthKey(`${apiUrl}?r=api/worker-foul/save-user-columns`, token);
        const formData = new FormData();

        formData.append("user_id", userId);
        formData.append("columns", JSON.stringify(userColumns));

        await axios.post(url, formData);

        commit("setUserColumns", userColumns);
    },
    async createViolation({ rootState, commit }, violationData) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/worker-foul/create`, token);
        const formData = appendViolationDataToFormData(violationData);

        const { data } = await axios.post(url, formData);

        if (data.success) {
            commit("putNewViolation", data.model);
        }

        return data;
    },
    async updateViolation({ rootState, commit }, violationData) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/worker-foul/update&id=${violationData.id}`, token);
        const formData = appendViolationUpdatingData(violationData);

        const { data } = await axios.post(url, formData);

        if (data.success) {
            commit("updateViolation", data.model);
        }

        return data;
    },
    async deleteViolation({ rootState, commit }, violationId) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/worker-foul/delete&id=${violationId}`, token);

        const { data } = await axios.get(url);

        if (data.success) {
            commit("deleteViolation", violationId);
        }
    },
    async addExplanatory({ rootState, commit }, explanatoryData) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/worker-foul/add-explanatory&id=${explanatoryData.violationId}`, token);
        const formData = new FormData();

        formData.append("text", explanatoryData.explanatoryContent);
        explanatoryData.files.forEach((file) => {
            formData.append("files[]", file);
        });

        const { data } = await axios.post(url, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        if (data) {
            commit("setExplanatoryToViolation", { explanatory: data, violationId: explanatoryData.violationId });
        }

        return data;
    },
    async updateViewOfExplanatory({ rootState, commit }, violationId) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/worker-foul/update&id=${violationId}`, token);
        const formData = new FormData();

        formData.append("explanatory_viewed", 1);

        const { data } = await axios.post(url, formData);

        if (data.success) {
            commit("setExplanatoryViewed", violationId);
        }

        return data;
    },
};

const mutations = {
    setCollections(state, collections) {
        state.collections = collections;
    },
    setFoulTypesCollection(state, foulTypes) {
        state.collections.foulTypes = foulTypes;
    },
    setFilialsCollection(state, filials) {
        state.collections.filials = filials;
    },
    setPositionsCollection(state, positions) {
        state.collections.positions = positions;
    },
    setWorkersCollection(state, workers) {
        state.collections.workers = workers;
    },
    setRecords(state, records) {
        state.records = [...state.records, ...records];
    },
    setFirstRecords(state, records) {
        state.records = records;
    },
    setColumns(state, columns) {
        state.columns = columns;
    },
    setUserColumns(state, columns) {
        state.userColumns = columns;
    },
    putNewViolation(state, violation) {
        state.records.unshift(violation);
    },
    updateViolation(state, updatedViolation) {
        const violationIndex = state.records.findIndex((r) => r.id === updatedViolation.id);
        state.records.splice(violationIndex, 1, updatedViolation);
    },
    deleteViolation(state, violationId) {
        const recordIndex = state.records.findIndex((r) => r.id === violationId);
        state.records.splice(recordIndex, 1);
    },
    setIsMoreDataAvailable(state, value) {
        state.isMoreDataAvailable = value;
    },
    setTotalCount(state, value) {
        state.totalCount = value;
    },
    setExplanatoryToViolation(state, { explanatory, violationId }) {
        const violation = state.records.find((record) => record.id === violationId);
        violation.explanatory = { ...explanatory, is_viewed: 0 };
    },
    setExplanatoryViewed(state, violationId) {
        const violation = state.records.find((record) => record.id === violationId);
        violation.explanatory = { ...violation.explanatory, is_viewed: 1 };
    },
    setQueryForGoogleSheets(state, query) {
        state.queryForGoogleSheets = query;
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
